import { cvaMerge } from '@payaca/utilities/cvaMerge';

type ButtonSize = 'xs' | 'sm' | 'md' | 'lg';
type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'quaternary';
type ButtonColour = 'primary' | 'positive' | 'negative' | 'job-brand';
type ButtonLinkColour = 'blue' | 'grey' | 'dark' | 'white';

/**
 * `linkColour` is only meaningful on the `link` variant, and `colour` only on
 * the others. The discriminated union enforces that at the type level so an
 * ignored prop cannot be passed silently.
 */
export type ButtonVariantProps =
  | {
      variant?: ButtonVariant;
      size?: ButtonSize;
      colour?: ButtonColour;
      linkColour?: never;
    }
  | {
      variant: 'link';
      size?: ButtonSize;
      linkColour?: ButtonLinkColour;
      colour?: never;
    };

// Permissive shape for the hook itself — the public discriminated union above
// guards the component's API, so the hook needn't re-discriminate.
type UseButtonClassNameOptions = {
  variant?: ButtonVariant | 'link';
  size?: ButtonSize;
  colour?: ButtonColour;
  linkColour?: ButtonLinkColour;
};

const outlinedVariantClasses = [
  'border-gray-200 bg-white font-medium shadow-sm hover:bg-gray-50',
  'disabled:hover:bg-white aria-disabled:hover:bg-white',
];

const buttonVariants = cvaMerge(
  [
    'font-inherit inline-flex cursor-pointer appearance-none items-center justify-center gap-x-2',
    'rounded-lg border bg-transparent px-4 py-3 text-base hover:no-underline',
    'disabled:cursor-not-allowed disabled:opacity-50 aria-disabled:cursor-not-allowed aria-disabled:opacity-50',
    'transition-shadow focus:ring-4 aria-expanded:ring-4',
  ],
  {
    variants: {
      variant: {
        primary: 'border-transparent font-semibold',
        secondary: outlinedVariantClasses,
        tertiary: [...outlinedVariantClasses, 'border-dashed'],
        quaternary:
          'border-transparent font-semibold disabled:bg-transparent aria-disabled:bg-transparent',
      },
      colour: {
        primary: '',
        positive: 'focus:ring-green-500/25 aria-expanded:ring-green-500/25',
        negative: 'focus:ring-red-500/25 aria-expanded:ring-red-500/25',
        'job-brand': 'focus:ring-job-brand/25 aria-expanded:ring-job-brand/25',
      },
      size: {
        xs: 'px-2 py-1.5',
        sm: 'px-3 py-2',
        md: '',
        lg: 'p-4 sm:p-5',
      },
    },
    compoundVariants: [
      // The `primary` colour is purple on the `primary` variant only, and body
      // text on every other variant.
      {
        variant: 'primary',
        colour: 'primary',
        class: [
          'bg-purple-base text-white hover:bg-purple-b0 disabled:bg-purple-base aria-disabled:bg-purple-base',
          'focus:ring-purple-a0/25 aria-expanded:ring-purple-a0/25',
        ],
      },
      {
        variant: ['secondary', 'tertiary', 'quaternary'],
        colour: 'primary',
        class: 'focus:ring-gray-800/25 aria-expanded:ring-gray-800/25',
      },

      // Primary
      {
        variant: 'primary',
        colour: 'positive',
        class:
          'bg-green-500 text-white hover:bg-green-600 disabled:bg-green-500 aria-disabled:bg-green-500',
      },
      {
        variant: 'primary',
        colour: 'negative',
        class:
          'bg-red-500 text-white hover:bg-red-600 disabled:bg-red-500 aria-disabled:bg-red-500',
      },
      {
        variant: 'primary',
        colour: 'job-brand',
        class:
          'bg-job-brand text-job-brand-readable hover:bg-job-brand-dark disabled:bg-job-brand-light aria-disabled:bg-job-brand-light',
      },

      // Secondary and tertiary
      {
        variant: ['secondary', 'tertiary'],
        colour: 'primary',
        class: 'text-gray-800',
      },
      {
        variant: ['secondary', 'tertiary'],
        colour: 'positive',
        class: 'text-green-500',
      },
      {
        variant: ['secondary', 'tertiary'],
        colour: 'negative',
        class: 'text-red-500',
      },
      {
        variant: ['secondary', 'tertiary'],
        colour: 'job-brand',
        class: 'text-job-brand',
      },

      // Quaternary
      {
        variant: 'quaternary',
        colour: 'primary',
        class:
          'text-gray-800 hover:bg-gray-100 disabled:text-gray-800 aria-disabled:text-gray-800',
      },
      {
        variant: 'quaternary',
        colour: 'positive',
        class:
          'text-green-500 hover:bg-green-100 hover:text-green-800 disabled:text-green-500 aria-disabled:text-green-500',
      },
      {
        variant: 'quaternary',
        colour: 'negative',
        class:
          'text-red-500 hover:bg-red-100 hover:text-red-800 disabled:text-red-500 aria-disabled:text-red-500',
      },
      {
        variant: 'quaternary',
        colour: 'job-brand',
        class:
          'text-job-brand hover:bg-job-brand-light hover:text-job-brand-readable disabled:text-job-brand/50 aria-disabled:text-job-brand/50',
      },
    ],
    defaultVariants: {
      variant: 'primary',
      colour: 'primary',
      size: 'md',
    },
  }
);

// A link is never padded, whatever its size.
const linkButtonVariants = cvaMerge(
  [
    'inline-flex cursor-pointer items-center justify-center gap-x-2 border-0 bg-transparent p-0 text-base font-medium',
    'hover:underline focus:underline active:underline',
    'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:no-underline aria-disabled:cursor-not-allowed aria-disabled:opacity-50',
  ],
  {
    variants: {
      linkColour: {
        blue: 'text-link',
        dark: 'text-blue-900 disabled:text-blue-900 aria-disabled:text-blue-900',
        grey: 'text-gray-500 disabled:text-gray-500 aria-disabled:text-gray-500',
        white: 'text-white disabled:text-white aria-disabled:text-white',
      },
      size: {
        xs: '',
        sm: 'text-sm',
        md: '',
        lg: '',
      },
    },
    defaultVariants: {
      linkColour: 'blue',
      size: 'md',
    },
  }
);

const useButtonClassName = ({
  variant,
  size,
  colour,
  linkColour,
}: UseButtonClassNameOptions) =>
  variant === 'link'
    ? linkButtonVariants({ linkColour, size })
    : buttonVariants({ variant, colour, size });

export default useButtonClassName;
